/**
 * disclosuresAdminApi — inline fetch wrappers for the M3 admin write surface.
 *
 * Tier-3 Disclosure Engine M4 (per
 * `docs/port-design/tier-3-disclosure-engine-plan.md`). M3 just landed 24
 * admin CRUD endpoints under `/v3/admin/disclosures/*` plus
 * `/v3/admin/merge-field-*` (groups + definitions) and a render preview
 * route at `/v3/admin/disclosures/preview`.
 *
 * Auth posture mirrors `riskReviewApi.ts`:
 *   - `Authorization: Bearer <msal>` (Path B token) — bearer-only; no
 *     function keys are shipped in the browser bundle.
 *   - Optional `x-pcb-officer-code` (Path C) — the AAD upn already covers
 *     Path B for Risk Mgmt staff, so the officer-code is only relevant when
 *     the operator's identity differs from their MSAL upn (rare).
 *
 * The composite envelope read surface (`disclosuresApi.ts`) stays the
 * single source-of-truth for catalog snapshots — every write here is
 * fire-and-forget; the caller is expected to invalidate the
 * DisclosuresContext via its `refresh()` after a successful save.
 *
 * Input shapes mirror `apps/PCBJxChangeApi/Models/AdminDisclosureModels.cs`
 * (M3 deliverable) one-for-one.
 *
 * Bill 2026-05-08
 */

import { getMsalToken } from '@/auth/msal';

// ─────────────────────────────────────────────────────────────────────────
// Input types — mirror Models/AdminDisclosureModels.cs (one record per
// upsert + status-transition + preview).
// ─────────────────────────────────────────────────────────────────────────

export interface DisclosureTemplateUpsertInput {
  code: string;
  displayName?: string | null;
  description?: string | null;
  layer?: string | null;
  multiplicity?: string | null;
  defaultPageCount?: number | null;
  signatureRequirement?: string | null;
  defaultSignatureCount?: number | null;
  contentSource?: string | null;
  variantKey?: string | null;
  productCategory?: string | null;
  applicableProducts?: string | null;
  applicableEntityTypes?: string | null;
  audience?: string | null;
  sortOrder?: number | null;
  isInternal?: boolean | null;
  isActive?: boolean | null;
}

export interface DisclosureTemplateVersionUpsertInput {
  versionId?: number | null;
  templateCode: string;
  versionNumber?: string | null;
  contentHtml?: string | null;
  fileUrl?: string | null;
  pageCount?: number | null;
  signatureCount?: number | null;
  variantSelector?: string | null;
  conditionExpression?: string | null;
  featureRequired?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  authoredBy?: string | null;
  notes?: string | null;
  status?: string | null;
}

export interface DisclosureVariantUpsertInput {
  variantKey: string;
  variantCode: string;
  customerType?: string | null;
  selectorExpr?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
}

export interface DisclosureSectionUpsertInput {
  code: string;
  title?: string | null;
  docCode?: string | null;
  category?: string | null;
  source?: string | null;
  bodyHtml?: string | null;
  version?: string | null;
  isActive?: boolean | null;
}

export interface MergeFieldGroupUpsertInput {
  code: string;
  name?: string | null;
  description?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
}

export interface MergeFieldDefinitionUpsertInput {
  code: string;
  label?: string | null;
  groupCode?: string | null;
  dataSource?: string | null;
  dataPath?: string | null;
  formatter?: string | null;
  isRequired?: boolean | null;
  isActive?: boolean | null;
}

export interface DisclosureWorkflowLinkUpsertInput {
  workflowCode: string;
  stepCode: string;
  templateCode: string;
  isMandatory?: boolean | null;
  conditionExpression?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
  notes?: string | null;
}

export interface DisclosureVersionTransitionInput {
  actor?: string | null;
  reason?: string | null;
}

export interface DisclosurePreviewInput {
  templateCode: string;
  audience?: string | null;
  planCode?: string | null;
  customerType?: string | null;
  sampleContextProfile?: string | null;
  mergeData?: Record<string, unknown> | null;
  includePdf?: boolean | null;
  /** Render exactly this version (any status). Omitted = the live (Active) version only. */
  versionId?: number | null;
  /**
   * Save-safety check: render this UNSAVED text of the (Draft) version instead of its saved
   * text. The server compiles it and lists unknown helpers / merge fields; nothing is saved.
   */
  contentHtml?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────
// Response shapes.
// ─────────────────────────────────────────────────────────────────────────

export interface AdminDisclosureWriteResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

export interface DisclosureVersionAuditRow {
  auditId?: string;
  actorRole?: string | null;
  versionId: number;
  timestamp: string;
  actor?: string | null;
  fromStatus?: string | null;
  toStatus: string;
  reason?: string | null;
  [k: string]: unknown;
}

export interface DisclosurePreviewDiagnostics {
  referencedTokens?: ReadonlyArray<string>;
  resolvedTokens?: ReadonlyArray<string>;
  optionalMisses?: ReadonlyArray<string>;
  requiredMisses?: ReadonlyArray<string>;
  warnings?: ReadonlyArray<string>;
  renderError?: string | null;
  /** True when the server rendered the contentHtml sent with the request (not the saved text). */
  checkedUnsavedText?: boolean;
  /** Handlebars compiler message when the text does not compile; null when it does. */
  compileError?: string | null;
  /** Helper calls ({{name arg}}) whose name the renderer does not know. */
  unknownHelpers?: ReadonlyArray<string>;
  /** Top-level merge-field paths that are not in the merge-field catalog. */
  unknownFields?: ReadonlyArray<string>;
}

export interface DisclosurePreviewResponse {
  templateCode?: string;
  resolvedBodyTemplateCode?: string;
  versionNumber?: string;
  versionId?: number;
  /** Status of the version that was rendered. */
  versionStatus?: string;
  /** True when the rendered version is the one live packages use. */
  isLiveVersion?: boolean;
  liveVersionId?: number | null;
  liveVersionNumber?: string | null;
  pdfError?: string | null;
  mergedHtml?: string;
  pdfBase64?: string;
  fieldCount?: number;
  diagnostics?: DisclosurePreviewDiagnostics;
}

// ─────────────────────────────────────────────────────────────────────────
// HTTP plumbing.
// ─────────────────────────────────────────────────────────────────────────

function getBaseUrl(): string {
  return (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
    'https://pcb-retail-api.azurewebsites.net/api'
  );
}

async function buildHeaders(): Promise<Headers> {
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'x-pcb-client-id': 'risk-mgmt-panel',
  });
  try {
    const token = await getMsalToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  } catch {
    // Silent — RoleGate enforced an authenticated session before mount.
  }
  return headers;
}

async function readResultJson<T>(resp: Response): Promise<AdminDisclosureWriteResult<T>> {
  const text = await resp.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = { raw: text };
    }
  }
  if (!resp.ok) {
    const errMsg =
      (body && typeof body === 'object' && 'error' in body
        ? String((body as { error?: unknown }).error)
        : null) ??
      (body && typeof body === 'object' && 'message' in body
        ? String((body as { message?: unknown }).message)
        : null) ??
      `HTTP ${resp.status}`;
    const errCode =
      body && typeof body === 'object' && 'code' in body
        ? String((body as { code?: unknown }).code)
        : undefined;
    return errCode !== undefined
      ? { success: false, error: errMsg, code: errCode }
      : { success: false, error: errMsg };
  }
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    if (typeof obj['success'] === 'boolean') {
      return body as AdminDisclosureWriteResult<T>;
    }
    return { success: true, data: body as T };
  }
  return { success: true };
}

async function postJson<T>(path: string, body: unknown): Promise<AdminDisclosureWriteResult<T>> {
  const url = `${getBaseUrl()}${path}`;
  const headers = await buildHeaders();
  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body ?? {}),
    });
    return await readResultJson<T>(resp);
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : String(err) };
  }
}

async function patchJson<T>(path: string, body: unknown): Promise<AdminDisclosureWriteResult<T>> {
  const url = `${getBaseUrl()}${path}`;
  const headers = await buildHeaders();
  try {
    const resp = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body ?? {}),
    });
    return await readResultJson<T>(resp);
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : String(err) };
  }
}

async function deleteJson<T>(path: string): Promise<AdminDisclosureWriteResult<T>> {
  const url = `${getBaseUrl()}${path}`;
  const headers = await buildHeaders();
  try {
    const resp = await fetch(url, {
      method: 'DELETE',
      headers,
    });
    return await readResultJson<T>(resp);
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : String(err) };
  }
}

async function getJson<T>(path: string): Promise<AdminDisclosureWriteResult<T>> {
  const url = `${getBaseUrl()}${path}`;
  const headers = await buildHeaders();
  try {
    const resp = await fetch(url, {
      method: 'GET',
      headers,
    });
    return await readResultJson<T>(resp);
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : String(err) };
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Templates (2 routes).
// ─────────────────────────────────────────────────────────────────────────

export function upsertTemplate(
  input: DisclosureTemplateUpsertInput,
): Promise<AdminDisclosureWriteResult> {
  return postJson('/v3/admin/disclosures/templates', input);
}

export function deleteTemplate(code: string): Promise<AdminDisclosureWriteResult> {
  return deleteJson(`/v3/admin/disclosures/templates/${encodeURIComponent(code)}`);
}

// ─────────────────────────────────────────────────────────────────────────
// Versions (8 routes — CRUD + state-machine + audit).
// ─────────────────────────────────────────────────────────────────────────

export function createVersion(
  input: DisclosureTemplateVersionUpsertInput,
): Promise<AdminDisclosureWriteResult> {
  return postJson('/v3/admin/disclosures/versions', input);
}

export function updateVersion(
  versionId: number,
  input: DisclosureTemplateVersionUpsertInput,
): Promise<AdminDisclosureWriteResult> {
  return patchJson(`/v3/admin/disclosures/versions/${versionId}`, input);
}

export function deleteVersion(versionId: number): Promise<AdminDisclosureWriteResult> {
  return deleteJson(`/v3/admin/disclosures/versions/${versionId}`);
}

export function submitVersion(
  versionId: number,
  actor: string,
  reason?: string,
): Promise<AdminDisclosureWriteResult> {
  const body: DisclosureVersionTransitionInput =
    reason !== undefined && reason !== null
      ? { actor, reason }
      : { actor };
  return postJson(`/v3/admin/disclosures/versions/${versionId}/submit`, body);
}

export function approveVersion(
  versionId: number,
  actor: string,
  reason?: string,
): Promise<AdminDisclosureWriteResult> {
  const body: DisclosureVersionTransitionInput =
    reason !== undefined && reason !== null
      ? { actor, reason }
      : { actor };
  return postJson(`/v3/admin/disclosures/versions/${versionId}/approve`, body);
}

export function activateVersion(
  versionId: number,
  actor: string,
  reason?: string,
): Promise<AdminDisclosureWriteResult> {
  const body: DisclosureVersionTransitionInput =
    reason !== undefined && reason !== null
      ? { actor, reason }
      : { actor };
  return postJson(`/v3/admin/disclosures/versions/${versionId}/activate`, body);
}

export function retireVersion(
  versionId: number,
  actor: string,
  reason: string,
): Promise<AdminDisclosureWriteResult> {
  return postJson(`/v3/admin/disclosures/versions/${versionId}/retire`, {
    actor,
    reason,
  } satisfies DisclosureVersionTransitionInput);
}

/** The route returns `{ versionId, audit: [...] }`, newest first. */
export async function versionAuditLog(
  versionId: number,
): Promise<AdminDisclosureWriteResult<{ rows: ReadonlyArray<DisclosureVersionAuditRow> }>> {
  const r = await getJson<{ audit?: ReadonlyArray<DisclosureVersionAuditRow> }>(
    `/v3/admin/disclosures/versions/${versionId}/audit`,
  );
  if (!r.success) return r.error !== undefined ? { success: false, error: r.error } : { success: false };
  return { success: true, data: { rows: r.data?.audit ?? [] } };
}

// ─────────────────────────────────────────────────────────────────────────
// Options — every closed value set the editors offer (1 route).
// ─────────────────────────────────────────────────────────────────────────

export interface VocabularyItem {
  value: string;
  label: string;
  meaning: string;
}

export interface DisclosureOptions {
  signatureRequirements: ReadonlyArray<VocabularyItem>;
  layers: ReadonlyArray<VocabularyItem>;
  presenceConditions: ReadonlyArray<VocabularyItem>;
  multiplicities: ReadonlyArray<VocabularyItem>;
  contentSources: ReadonlyArray<VocabularyItem>;
  audiences: ReadonlyArray<VocabularyItem>;
  universalFloorCodes: ReadonlyArray<string>;
  productCategories: ReadonlyArray<string>;
  products: ReadonlyArray<{ code: string; name: string; category: string; isActive: boolean }>;
  entityTypes: ReadonlyArray<{ value: string; label: string; isActive: boolean }>;
  entityRequiredDocs: ReadonlyArray<{ customerType: string; docCode: string; isMandatory: boolean }>;
}

export function getDisclosureOptions(): Promise<AdminDisclosureWriteResult<DisclosureOptions>> {
  return getJson<DisclosureOptions>('/v3/admin/disclosures/options');
}

// ─────────────────────────────────────────────────────────────────────────
// Variants (2 routes).
// ─────────────────────────────────────────────────────────────────────────

export function upsertVariant(
  input: DisclosureVariantUpsertInput,
): Promise<AdminDisclosureWriteResult> {
  return postJson('/v3/admin/disclosures/variants', input);
}

export function deleteVariant(
  variantKey: string,
  variantCode: string,
  // backend route is composite (key,code); customerType is admin-side
  // qualifier carried in upsert body, not in DELETE path. Optional arg kept
  // for forward-compat when the route grows it.
  _customerType?: string,
): Promise<AdminDisclosureWriteResult> {
  return deleteJson(
    `/v3/admin/disclosures/variants/${encodeURIComponent(variantKey)}/${encodeURIComponent(variantCode)}`,
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sections (2 routes).
// ─────────────────────────────────────────────────────────────────────────

export function upsertSection(
  input: DisclosureSectionUpsertInput,
): Promise<AdminDisclosureWriteResult> {
  return postJson('/v3/admin/disclosures/sections', input);
}

export function deleteSection(code: string): Promise<AdminDisclosureWriteResult> {
  return deleteJson(`/v3/admin/disclosures/sections/${encodeURIComponent(code)}`);
}

// ─────────────────────────────────────────────────────────────────────────
// Merge field groups (2 routes).
// ─────────────────────────────────────────────────────────────────────────

export function upsertGroup(
  input: MergeFieldGroupUpsertInput,
): Promise<AdminDisclosureWriteResult> {
  return postJson('/v3/admin/merge-field-groups', input);
}

export function deleteGroup(code: string): Promise<AdminDisclosureWriteResult> {
  return deleteJson(`/v3/admin/merge-field-groups/${encodeURIComponent(code)}`);
}

// ─────────────────────────────────────────────────────────────────────────
// Merge field definitions (2 routes).
// ─────────────────────────────────────────────────────────────────────────

export function upsertDefinition(
  input: MergeFieldDefinitionUpsertInput,
): Promise<AdminDisclosureWriteResult> {
  return postJson('/v3/admin/merge-field-definitions', input);
}

export function deleteDefinition(code: string): Promise<AdminDisclosureWriteResult> {
  return deleteJson(`/v3/admin/merge-field-definitions/${encodeURIComponent(code)}`);
}

// ─────────────────────────────────────────────────────────────────────────
// Workflow links (2 routes).
// ─────────────────────────────────────────────────────────────────────────

export function upsertLink(
  input: DisclosureWorkflowLinkUpsertInput,
): Promise<AdminDisclosureWriteResult> {
  return postJson('/v3/admin/disclosures/workflow-links', input);
}

export function deleteLink(
  workflowCode: string,
  stepCode: string,
  templateCode: string,
): Promise<AdminDisclosureWriteResult> {
  return deleteJson(
    `/v3/admin/disclosures/workflow-links/${encodeURIComponent(workflowCode)}/${encodeURIComponent(stepCode)}/${encodeURIComponent(templateCode)}`,
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Preview render (1 route).
// ─────────────────────────────────────────────────────────────────────────

export function previewRender(
  input: DisclosurePreviewInput,
): Promise<AdminDisclosureWriteResult<DisclosurePreviewResponse>> {
  return postJson<DisclosurePreviewResponse>(
    '/v3/admin/disclosures/preview',
    input,
  );
}
